import { Datepicker } from 'flowbite'
import { lenientFormat } from './flowbite_datepicker_format'

const sameYear = (date, now) => date.getFullYear() === now.getFullYear()
const markCurrent = (matches) => (date) =>
  matches(date, new Date()) ? 'today' : undefined

export function enhancePicker(datepicker) {
  const { picker } = datepicker
  if (!picker || Object.hasOwn(picker, 'setViewSwitchLabel')) return

  const { config } = datepicker
  const { viewSwitch } = picker.controls
  viewSwitch.className = 'view-switch kit-datepicker-switch'
  picker.setViewSwitchLabel = (label) => {
    viewSwitch.textContent = label
    viewSwitch.disabled = picker.currentView.id === config.maxView
  }
  picker.render(false)
}

const liveDatepickers = new WeakMap()
const vanillaDatepickers = (instance) =>
  instance?.datepickers || (instance ? [instance] : [])

const originalInit = Datepicker.prototype.init
Datepicker.prototype.init = function () {
  originalInit.call(this)
  vanillaDatepickers(this._datepickerInstance).forEach((datepicker) => {
    enhancePicker(datepicker)
    liveDatepickers.set(datepicker.element, datepicker)
  })
}

const originalDestroy = Datepicker.prototype.destroy
Datepicker.prototype.destroy = function () {
  const destroyed = vanillaDatepickers(this._datepickerInstance)
  originalDestroy.call(this)
  // initFlowbite re-runs with override, destroying the old instance after the new one claimed the element.
  destroyed.forEach(({ element }) => {
    const live = liveDatepickers.get(element)
    if (destroyed.includes(live)) liveDatepickers.delete(element)
    else if (live) element.datepicker = live
  })
}

Datepicker.prototype._getDatepickerOptions = function (options) {
  var datepickerOptions = {}
  if (options.buttons) {
    datepickerOptions.todayBtn = true
    datepickerOptions.clearBtn = true
    if (options.autoSelectToday) {
      datepickerOptions.todayBtnMode = 1
    }
  }
  if (options.autohide) {
    datepickerOptions.autohide = true
  }
  if (options.format) {
    datepickerOptions.format = lenientFormat(options.format)
  }
  // Formats like "D, dd M yyyy" contain the default "," delimiter, which would split one date in two.
  datepickerOptions.dateDelimiter = '\n'
  if (options.maxDate) {
    datepickerOptions.maxDate = options.maxDate
  }
  if (options.minDate) {
    datepickerOptions.minDate = options.minDate
  }
  if (options.orientation) {
    datepickerOptions.orientation = options.orientation
  }
  if (options.title) {
    datepickerOptions.title = options.title
  }
  if (options.language) {
    datepickerOptions.language = options.language
  }
  // Portaled pickers only re-place themselves on resize, so one anchored to a sticky element
  // must render in place instead. Opt in per input with data-datepicker-container.
  var explicitContainer = this._datepickerEl.dataset.datepickerContainer
  var modalContainer = this._datepickerEl.closest('#flowbite_modal_pickers')
  datepickerOptions.container =
    explicitContainer ||
    (modalContainer ? '#flowbite_modal_pickers' : '#flowbite_container_pickers')
  datepickerOptions.todayHighlight = true
  datepickerOptions.beforeShowMonth = markCurrent(
    (date, now) => sameYear(date, now) && date.getMonth() === now.getMonth(),
  )
  datepickerOptions.beforeShowYear = markCurrent(sameYear)
  datepickerOptions.beforeShowDecade = markCurrent(
    (date, now) =>
      Math.floor(date.getFullYear() / 10) ===
      Math.floor(now.getFullYear() / 10),
  )
  if (document.body.classList.contains('finance-page')) {
    datepickerOptions.updateOnBlur = false
    datepickerOptions.showOnFocus = false
  }
  return datepickerOptions
}

const pickerInput = (el) =>
  el?.datepicker && el.datepicker.inputField === el ? el : null

function committedText({ config, dates }) {
  if (typeof config.format.toDisplay !== 'function') return null
  return dates
    .map((date) =>
      config.format.toDisplay(new Date(date), config.format, config.locale),
    )
    .join(config.dateDelimiter)
}

function markValidity(input) {
  const committed = committedText(input.datepicker)
  if (committed === null) return

  const value = input.value.trim()
  const invalid = value !== '' && value !== committed

  if (invalid) {
    input.setAttribute('aria-invalid', 'true')
    if (input.classList.contains('kit-input'))
      input.classList.add('kit-input-error')
    input.dataset.datepickerInvalid = 'true'
  } else if (input.dataset.datepickerInvalid) {
    input.removeAttribute('aria-invalid')
    input.classList.remove('kit-input-error')
    delete input.dataset.datepickerInvalid
  }
}

const afterPicker = (input) =>
  setTimeout(() => input.isConnected && markValidity(input))

function navigatedGrid({ dates, config, picker }) {
  const anchor = dates.length ? dates[dates.length - 1] : config.defaultViewDate
  return picker.viewDate !== anchor
}

function submitsOnEnter(input) {
  const { datepicker } = input
  if (!input.form) return false
  if (datepicker.editMode || !datepicker.picker.active) return true
  return (
    input.value.trim() !== committedText(datepicker) ||
    !navigatedGrid(datepicker)
  )
}

const defaultSubmitter = (form) =>
  Array.from(form.ownerDocument.querySelectorAll('button, input')).find(
    (el) => el.form === form && (el.type === 'submit' || el.type === 'image'),
  )

function commitAndSubmit(event, input) {
  const { datepicker } = input
  const submitter = defaultSubmitter(input.form)
  const submits = submitter && !submitter.disabled
  event.preventDefault()
  if (submits) event.stopImmediatePropagation()
  datepicker.exitEditMode()
  datepicker.update()
  markValidity(input)
  if (input.dataset.datepickerInvalid || !submits) return

  datepicker.hide()
  input.form.requestSubmit(submitter)
}

window.addEventListener(
  'keydown',
  (event) => {
    const input = pickerInput(event.target)
    if (!input) return
    if (event.key === 'Enter' && submitsOnEnter(input)) {
      commitAndSubmit(event, input)
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      afterPicker(input)
    }
  },
  true,
)

const onPointerOutside = (event) => {
  const input = pickerInput(document.activeElement)
  if (!input) return
  const path = event.composedPath()
  if (path.includes(input) || path.includes(input.datepicker.picker.element))
    return
  setTimeout(() => {
    if (!input.isConnected) return
    markValidity(input)
    // Vanilla's outside click skips its hide when the typed text does not parse.
    input.datepicker.hide()
  })
}
window.addEventListener('mousedown', onPointerOutside, true)
window.addEventListener('touchstart', onPointerOutside, true)

window.addEventListener(
  'changeDate',
  (event) => {
    const input = pickerInput(event.target)
    if (input) markValidity(input)
  },
  true,
)
